import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_ENTER_DELAY = 1000;
const NAV_EXIT_DURATION = 400;

const Navbar = () => {
  const [visible, setVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: "Projects", path: "/projects" },
    { name: "Research", path: "/research" },
   // { name: "Elements", path: "/elements" },
    { name: "Blogs", path: "/blog" },
    { name: "Studio", path: "/studio" },
    { name: "Contact", path: "/contact" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), NAV_ENTER_DELAY);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isNavigating) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isNavigating]);

  const handleNavClick = (path) => {
    if (path === location.pathname) return;

    setMenuOpen(false);
    setIsNavigating(true);
    setVisible(false);

    setTimeout(() => {
      navigate(path);
      setIsNavigating(false);
    }, NAV_EXIT_DURATION);
  };

  return (
    <>
      <motion.nav
        animate={{ y: visible ? 0 : -100, opacity: visible ? 1 : 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="
          fixed top-0 left-0 right-0 z-50
          bg-black/40 border-b border-white/20 shadow-lg
          backdrop-blur-sm
        "
        style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      >
        {/* Inner content wrapper */}
        <div className="flex items-center justify-between px-8 md:px-12 py-4">
          {/* LOGO */}
          <button onClick={() => handleNavClick("/")} aria-label="Studio Sharan – Home">
            <img src="/images/LOGO.svg" alt="Studio Sharan" className="h-6 w-auto" />
          </button>

          {/* DESKTOP LINKS */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.path)}
                className={`
                  relative font-medium transition
                  text-gray-200 hover:text-gray-100
                  ${
                    location.pathname === item.path ? "after:w-full" : "after:w-0"
                  }
                  after:absolute after:left-0 after:-bottom-1 after:h-[2px]
                  after:bg-white after:transition-all after:duration-300
                `}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* MOBILE TOGGLE */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg bg-black/40 transition"
          >
            {menuOpen ? <X size={22} color="white" /> : <Menu size={22} color="white" />}
          </button>
        </div>
      </motion.nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed top-[64px] left-0 right-0 z-40 bg-black/50 border-t border-white/20 md:hidden"
          >
            <div className="flex flex-col space-y-4 px-8 py-6">
              {navLinks.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.path)}
                  className={`text-lg text-left font-medium ${
                    location.pathname === item.path ? "text-white" : "text-gray-300"
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
